import type React from 'react'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  useCreateBankAccountMutation,
  useCreateSystemBankAccountMutation,
} from '../redux/services/coreApi'
import { RootState } from '../redux/store'
import type {
  BankAccountDetailsDto,
  BankAccountDetailsType,
  CurrencyDto,
} from '../types/coreApi-types'
import { payoutRequiresIban } from '../utils/bankAccountPayout'
import DefaultModal from './DefaultModal'
import SegmentTab, { type SegmentTabItemProps } from './SegmentTab'

export type NonNullableFields<T> = {
  [P in keyof T]: NonNullable<T[P]>
}

const initialBankAccountDetails: Required<
  NonNullableFields<BankAccountDetailsDto>
> = {
  registration: '',
  accountNumber: '',
  bic: '',
  iban: '',
  recipientAddress: '',
  recipientCity: '',
  recipientCountry: '',
  recipientName: '',
  recipientZipCode: '',
  code: '',
}

interface AddBankAccountModalProps {
  isOpen: boolean
  onClose: () => void
  systemBankAccount?: boolean
  /** The currency the account is being added to receive; a non-DKK payout only offers the IBAN form. */
  payoutCurrency?: CurrencyDto
}

const AddBankAccountModal: React.FC<AddBankAccountModalProps> = ({
  isOpen,
  onClose,
  systemBankAccount = false,
  payoutCurrency,
}) => {
  const sessionUser = useSelector((state: RootState) => state.session.user)

  const ibanOnly = Boolean(payoutCurrency && payoutRequiresIban(payoutCurrency))
  const defaultBankAccountDetailsType: BankAccountDetailsType = ibanOnly
    ? 'InternationalBankAccountDetails'
    : 'DomesticBankAccountDetails'

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [bankAccountDetails, setBankAccountDetails] = useState(
    initialBankAccountDetails
  )
  const [bankAccountDetailsType, setBankAccountDetailsType] =
    useState<BankAccountDetailsType>(defaultBankAccountDetailsType)

  const [createBankAccount, { isLoading: isLoadingBankAccount }] =
    useCreateBankAccountMutation()
  const [createSystemBankAccount, { isLoading: isLoadingSystemBankAccount }] =
    useCreateSystemBankAccountMutation()

  const isLoading = isLoadingBankAccount || isLoadingSystemBankAccount

  const onSubmit = async () => {
    if (systemBankAccount) {
      await createSystemBankAccount({
        bankAccountDetailsType,
        name,
        description,
        ...bankAccountDetails,
      }).unwrap()
    } else {
      await createBankAccount({
        userId: sessionUser.id,
        body: {
          bankAccountDetailsType,
          name,
          description,
          ...bankAccountDetails,
        },
      }).unwrap()
    }
    onClose()
  }

  const tabs: SegmentTabItemProps[] = [
    {
      title: 'Danish',
      isActive: bankAccountDetailsType === 'DomesticBankAccountDetails',
      onClick: () => setBankAccountDetailsType('DomesticBankAccountDetails'),
    },
    {
      title: 'International',
      isActive: bankAccountDetailsType === 'InternationalBankAccountDetails',
      onClick: () =>
        setBankAccountDetailsType('InternationalBankAccountDetails'),
    },
  ]

  useEffect(() => {
    if (isOpen) {
      setBankAccountDetailsType(defaultBankAccountDetailsType)
      setBankAccountDetails(initialBankAccountDetails)
    }
  }, [isOpen, defaultBankAccountDetailsType])

  return (
    <DefaultModal
      isLoading={isLoading}
      isLoadingText="Adding your Bank Account..."
      isOpen={isOpen}
      onClose={onClose}
      title="Add new bank account"
      whiteBG
    >
      {!ibanOnly && <SegmentTab tabs={tabs} />}
      <div className="p-5 pt-0 space-y-5 h-[600px] ">
        <div className="max-h-[450px] overflow-y-auto">
          {ibanOnly && (
            <p className="mb-4 text-description">
              Payouts in {payoutCurrency?.isoCode} can only be sent to an IBAN,
              so we need your account's IBAN and BIC. Danish bank accounts have
              an IBAN too (it starts with DK) — you'll find it in your online
              bank.
            </p>
          )}
          <p className="mb-1 text-description">Name of your bank</p>
          <input
            type="text"
            className="input mb-3"
            value={name}
            onChange={e => setName(e.target.value)}
          />
          <p className="mb-1 text-description">Description (optional)</p>
          <input
            type="text"
            className="input mb-3"
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
          {bankAccountDetailsType === 'DomesticBankAccountDetails' && (
            <>
              <p className="mb-1 text-description">Registration number</p>
              <input
                type="text"
                className="input mb-3"
                value={bankAccountDetails.registration}
                onChange={e =>
                  setBankAccountDetails(bad => ({
                    ...bad,
                    registration: e.target.value,
                  }))
                }
              />
              <p className="mb-1 text-description">Account number</p>
              <input
                type="text"
                className="input"
                value={bankAccountDetails.accountNumber}
                onChange={e =>
                  setBankAccountDetails(bad => ({
                    ...bad,
                    accountNumber: e.target.value,
                  }))
                }
              />
            </>
          )}
          {bankAccountDetailsType === 'InternationalBankAccountDetails' && (
            <>
              <p className="mb-1 text-description">IBAN</p>
              <input
                type="text"
                className="input mb-3"
                value={bankAccountDetails.iban}
                onChange={e =>
                  setBankAccountDetails(bad => ({
                    ...bad,
                    iban: e.target.value,
                  }))
                }
              />
              <p className="mb-1 text-description">BIC</p>
              <input
                type="text"
                className="input mb-3"
                value={bankAccountDetails.bic}
                onChange={e =>
                  setBankAccountDetails(bad => ({
                    ...bad,
                    bic: e.target.value,
                  }))
                }
              />
              <p className="mb-1 text-description">Recipient Name</p>
              <input
                type="text"
                className="input mb-3"
                value={bankAccountDetails.recipientName}
                onChange={e =>
                  setBankAccountDetails(bad => ({
                    ...bad,
                    recipientName: e.target.value,
                  }))
                }
              />
              <p className="mb-1 text-description">Recipient Address</p>
              <input
                type="text"
                className="input mb-3"
                value={bankAccountDetails.recipientAddress}
                onChange={e =>
                  setBankAccountDetails(bad => ({
                    ...bad,
                    recipientAddress: e.target.value,
                  }))
                }
              />
              <p className="mb-1 text-description">Recipient Zip Code</p>
              <input
                type="text"
                className="input mb-3"
                value={bankAccountDetails.recipientZipCode}
                onChange={e =>
                  setBankAccountDetails(bad => ({
                    ...bad,
                    recipientZipCode: e.target.value,
                  }))
                }
              />
              <p className="mb-1 text-description">Recipient City</p>
              <input
                type="text"
                className="input mb-3"
                value={bankAccountDetails.recipientCity}
                onChange={e =>
                  setBankAccountDetails(bad => ({
                    ...bad,
                    recipientCity: e.target.value,
                  }))
                }
              />
              <p className="mb-1 text-description">Recipient Country</p>
              <input
                type="text"
                className="input mb-3"
                value={bankAccountDetails.recipientCountry}
                onChange={e =>
                  setBankAccountDetails(bad => ({
                    ...bad,
                    recipientCountry: e.target.value,
                  }))
                }
              />
              <p className="mb-1 text-description">Code (optional)</p>
              <input
                type="text"
                className="input"
                value={bankAccountDetails.code}
                onChange={e =>
                  setBankAccountDetails(bad => ({
                    ...bad,
                    code: e.target.value,
                  }))
                }
              />
            </>
          )}
        </div>

        <button disabled={false} onClick={onSubmit} className="button">
          Submit
        </button>
      </div>
    </DefaultModal>
  )
}

export default AddBankAccountModal
